import type {
  BusinessToolApiConfig,
  BusinessToolAuthType,
  BusinessToolDetail,
  BusinessToolHttpMethod,
  BusinessToolParameter,
  BusinessToolParameterType,
  BusinessToolPreRequest,
  BusinessToolResponseBodyFormat,
  CreateBusinessToolRequest,
} from '@circuitry-ai/doc-data';

export type BusinessToolExecutionMode = NonNullable<CreateBusinessToolRequest['execution_mode']>;

export const BUSINESS_TOOL_PARAMETER_TYPES: BusinessToolParameterType[] = [
  'string',
  'integer',
  'boolean',
  'array',
  'email',
  'phone_number',
  'url',
  'date',
  'datetime',
];

export const BUSINESS_TOOL_HTTP_METHODS: BusinessToolHttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export const BUSINESS_TOOL_AUTH_TYPES: BusinessToolAuthType[] = [
  'none',
  'bearer',
  'api_key',
  'basic',
  'oauth2',
  'custom',
  'custom_token_db',
];

export const BUSINESS_TOOL_EXECUTION_MODES: BusinessToolExecutionMode[] = [
  'immediate',
  'deferred',
];

export type KeyValuePair = { id: string; key: string; value: string };

export type ParameterFormRow = BusinessToolParameter & { id: string };

export type PreRequestFormRow = {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  method: BusinessToolHttpMethod;
  headers: KeyValuePair[];
  body_template: string;
  query_params: KeyValuePair[];
  extract_fields: KeyValuePair[];
  timeout_seconds: number;
  cache_enabled: boolean;
  cache_key: string;
};

export type AuthFormState = {
  type: BusinessToolAuthType;
  token: string;
  api_key: string;
  header_name: string;
  username: string;
  password: string;
  client_id: string;
  client_secret: string;
  token_url: string;
  scope: string;
  grant_type: 'client_credentials' | 'authorization_code';
  headers: KeyValuePair[];
  credential_id: string;
};

export type BusinessToolFormState = {
  name: string;
  description: string;
  engaging_words: string;
  execution_mode: BusinessToolExecutionMode;
  deferred_ack_message: string;
  parameters: ParameterFormRow[];
  pre_requests: PreRequestFormRow[];
  base_url: string;
  endpoint: string;
  method: BusinessToolHttpMethod;
  timeout_seconds: number;
  query_params: KeyValuePair[];
  body_template: string;
  authentication: AuthFormState;
  response_body_format: BusinessToolResponseBodyFormat;
  sse_final_event_name: string;
  response_extract_path: string;
  response_parse_inner_json: boolean;
  response_field_path: string;
  response_max_chars: string;
  success_message: string;
  error_message: string;
};

export function createId(): string {
  return globalThis.crypto.randomUUID();
}

export function createEmptyKeyValue(): KeyValuePair {
  return { id: createId(), key: '', value: '' };
}

export function createEmptyParameter(): ParameterFormRow {
  return {
    id: createId(),
    name: '',
    type: 'string',
    description: '',
    required: true,
    examples: [],
  };
}

export function createEmptyPreRequest(): PreRequestFormRow {
  return {
    id: createId(),
    name: '',
    description: '',
    endpoint: '',
    method: 'POST',
    headers: [],
    body_template: '',
    query_params: [],
    extract_fields: [createEmptyKeyValue()],
    timeout_seconds: 10,
    cache_enabled: false,
    cache_key: '',
  };
}

export function createDefaultAuthState(): AuthFormState {
  return {
    type: 'none',
    token: '',
    api_key: '',
    header_name: 'X-API-Key',
    username: '',
    password: '',
    client_id: '',
    client_secret: '',
    token_url: '',
    scope: '',
    grant_type: 'client_credentials',
    headers: [],
    credential_id: '',
  };
}

export function createDefaultBusinessToolForm(): BusinessToolFormState {
  return {
    name: '',
    description: '',
    engaging_words: 'Processing your request...',
    execution_mode: 'immediate',
    deferred_ack_message: '',
    parameters: [createEmptyParameter()],
    pre_requests: [],
    base_url: '',
    endpoint: '',
    method: 'POST',
    timeout_seconds: 30,
    query_params: [],
    body_template: '',
    authentication: createDefaultAuthState(),
    response_body_format: 'json',
    sse_final_event_name: 'workflow_finished',
    response_extract_path: '',
    response_parse_inner_json: false,
    response_field_path: '',
    response_max_chars: '12000',
    success_message: '',
    error_message: '',
  };
}

export function normalizeToolName(raw: string): string {
  return raw
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .replace(/_+/g, '_');
}

export function isValidToolName(name: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(name) && name.length > 0;
}

export function isValidParameterName(name: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(name) && name.length > 0;
}

export function keyValuePairsToRecord(pairs: KeyValuePair[]): Record<string, string> | undefined {
  const entries = pairs
    .map((pair) => [pair.key.trim(), pair.value] as const)
    .filter(([key]) => key.length > 0);
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
}

export function parseJsonObject(
  raw: string,
  fieldLabel: string
): { value: Record<string, unknown> | null; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null };
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: null, error: `${fieldLabel} must be a JSON object` };
    }
    return { value: parsed as Record<string, unknown> };
  } catch {
    return { value: null, error: `${fieldLabel} must be valid JSON` };
  }
}

export function extractPlaceholders(text: string): string[] {
  const matches = text.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g);
  const found = new Set<string>();
  for (const match of matches) {
    const name = match[1]?.trim();
    if (name) found.add(name);
  }
  return [...found];
}

export function collectTemplateStrings(form: BusinessToolFormState): string[] {
  const texts: string[] = [form.body_template, form.success_message, form.error_message];
  for (const pair of form.query_params) {
    texts.push(pair.value);
  }
  for (const pre of form.pre_requests) {
    texts.push(pre.body_template, pre.cache_key);
    for (const pair of [...pre.headers, ...pre.query_params]) {
      texts.push(pair.value);
    }
  }
  if (form.authentication.type === 'custom') {
    for (const pair of form.authentication.headers) {
      texts.push(pair.value);
    }
  }
  return texts;
}

export function getAvailablePlaceholders(form: BusinessToolFormState): string[] {
  const placeholders: string[] = [];
  for (const param of form.parameters) {
    const name = param.name.trim();
    if (name) placeholders.push(name);
  }
  for (const pre of form.pre_requests) {
    for (const field of pre.extract_fields) {
      const key = field.key.trim();
      if (key) placeholders.push(`pre_request.${key}`);
    }
  }
  placeholders.push('auth_token');
  return placeholders;
}

export function findUnresolvedPlaceholders(form: BusinessToolFormState): string[] {
  const available = new Set(getAvailablePlaceholders(form));
  available.add('response');
  const unresolved = new Set<string>();
  for (const text of collectTemplateStrings(form)) {
    for (const placeholder of extractPlaceholders(text)) {
      if (!available.has(placeholder)) {
        unresolved.add(placeholder);
      }
    }
  }
  return [...unresolved];
}

export function buildCreateBusinessToolPayload(
  form: BusinessToolFormState
): { payload?: CreateBusinessToolRequest; errors: string[] } {
  const errors: string[] = [];
  const name = normalizeToolName(form.name.trim());
  if (!isValidToolName(name)) {
    errors.push('Tool name is required (letters, numbers, underscores only)');
  }
  if (!form.description.trim()) {
    errors.push('Description is required');
  }
  if (!form.base_url.trim()) {
    errors.push('Base URL is required');
  }
  if (!form.endpoint.trim()) {
    errors.push('Endpoint is required');
  }
  if (form.timeout_seconds < 1 || form.timeout_seconds > 300) {
    errors.push('API timeout must be between 1 and 300 seconds');
  }

  const paramNames = new Set<string>();
  const parameters: BusinessToolParameter[] = [];
  for (const param of form.parameters) {
    const paramName = param.name.trim();
    if (!paramName) {
      errors.push('Each parameter needs a name');
      continue;
    }
    if (!isValidParameterName(paramName)) {
      errors.push(`Invalid parameter name: ${paramName}`);
      continue;
    }
    if (paramNames.has(paramName)) {
      errors.push(`Duplicate parameter name: ${paramName}`);
      continue;
    }
    paramNames.add(paramName);
    if (!param.description.trim()) {
      errors.push(`Parameter "${paramName}" needs a description`);
    }
    parameters.push({
      name: paramName,
      type: param.type,
      description: param.description.trim(),
      required: param.required,
      ...(param.examples && param.examples.length > 0 ? { examples: param.examples } : {}),
    });
  }

  const pre_requests: BusinessToolPreRequest[] = [];
  for (const [index, pre] of form.pre_requests.entries()) {
    const label = `Pre-request ${index + 1}`;
    if (!pre.name.trim()) errors.push(`${label}: name is required`);
    if (!pre.description.trim()) errors.push(`${label}: description is required`);
    if (!pre.endpoint.trim()) errors.push(`${label}: endpoint is required`);
    if (pre.timeout_seconds < 1 || pre.timeout_seconds > 60) {
      errors.push(`${label}: timeout must be between 1 and 60 seconds`);
    }

    const extract_fields = keyValuePairsToRecord(pre.extract_fields);
    if (!extract_fields || Object.keys(extract_fields).length === 0) {
      errors.push(`${label}: at least one extract field is required`);
    }

    const bodyParsed = parseJsonObject(pre.body_template, `${label} body template`);
    if (bodyParsed.error) errors.push(bodyParsed.error);

    pre_requests.push({
      name: pre.name.trim(),
      description: pre.description.trim(),
      endpoint: pre.endpoint.trim(),
      method: pre.method,
      headers: keyValuePairsToRecord(pre.headers) ?? null,
      body_template: bodyParsed.value,
      query_params: keyValuePairsToRecord(pre.query_params) ?? null,
      extract_fields: extract_fields ?? {},
      timeout_seconds: pre.timeout_seconds,
      cache_config: {
        enabled: pre.cache_enabled,
        ...(pre.cache_enabled && pre.cache_key.trim()
          ? { cache_key: pre.cache_key.trim() }
          : {}),
      },
    });
  }

  const bodyParsed = parseJsonObject(form.body_template, 'API body template');
  if (bodyParsed.error) errors.push(bodyParsed.error);

  if (form.authentication.type === 'custom_token_db' && !form.authentication.credential_id) {
    errors.push('Select a stored credential for custom_token_db authentication');
  }
  if (form.authentication.type === 'bearer' && !form.authentication.token.trim()) {
    errors.push('Bearer token is required');
  }
  if (form.authentication.type === 'api_key' && !form.authentication.api_key.trim()) {
    errors.push('API key is required');
  }
  if (form.authentication.type === 'basic') {
    if (!form.authentication.username.trim() || !form.authentication.password.trim()) {
      errors.push('Basic auth requires username and password');
    }
  }
  if (form.authentication.type === 'oauth2') {
    if (
      !form.authentication.client_id.trim() ||
      !form.authentication.client_secret.trim() ||
      !form.authentication.token_url.trim()
    ) {
      errors.push('OAuth2 requires client_id, client_secret, and token_url');
    }
  }

  const maxCharsRaw = form.response_max_chars.trim();
  let response_max_chars: number | null = null;
  if (maxCharsRaw) {
    const parsed = Number(maxCharsRaw);
    if (!Number.isFinite(parsed) || parsed < 1) {
      errors.push('Response max chars must be a positive number');
    } else {
      response_max_chars = parsed;
    }
  }

  if (errors.length > 0) {
    return { errors };
  }

  const auth = form.authentication;
  let authentication: BusinessToolApiConfig['authentication'];
  switch (auth.type) {
    case 'none':
      authentication = { type: 'none' };
      break;
    case 'bearer':
      authentication = { type: 'bearer', token: auth.token };
      break;
    case 'api_key':
      authentication = {
        type: 'api_key',
        api_key: auth.api_key,
        header_name: auth.header_name || 'X-API-Key',
      };
      break;
    case 'basic':
      authentication = {
        type: 'basic',
        username: auth.username,
        password: auth.password,
      };
      break;
    case 'oauth2':
      authentication = {
        type: 'oauth2',
        client_id: auth.client_id,
        client_secret: auth.client_secret,
        token_url: auth.token_url,
        grant_type: auth.grant_type,
        ...(auth.scope.trim() ? { scope: auth.scope.trim() } : {}),
      };
      break;
    case 'custom':
      authentication = {
        type: 'custom',
        headers: keyValuePairsToRecord(auth.headers) ?? {},
      };
      break;
    case 'custom_token_db':
      authentication = {
        type: 'custom_token_db',
        credential_id: auth.credential_id,
      };
      break;
    default:
      authentication = { type: 'none' };
  }

  const api_config: BusinessToolApiConfig = {
    base_url: form.base_url.trim(),
    endpoint: form.endpoint.trim(),
    method: form.method,
    timeout_seconds: form.timeout_seconds,
    authentication,
    query_params: keyValuePairsToRecord(form.query_params) ?? {},
    body_template: bodyParsed.value,
    success_message: form.success_message.trim() || undefined,
    error_message: form.error_message.trim() || undefined,
    response_body_format: form.response_body_format,
    response_parse_inner_json: form.response_parse_inner_json,
    response_extract_path: form.response_extract_path.trim() || null,
    response_field_path: form.response_extract_path.trim()
      ? form.response_field_path.trim() || null
      : null,
    response_max_chars,
    ...(form.response_body_format === 'sse'
      ? { sse_final_event_name: form.sse_final_event_name.trim() || 'workflow_finished' }
      : {}),
  };

  const deferred_ack_message =
    form.execution_mode === 'deferred' ? form.deferred_ack_message.trim() : '';

  return {
    payload: {
      name,
      description: form.description.trim(),
      engaging_words: form.engaging_words.trim() || 'Processing your request...',
      execution_mode: form.execution_mode,
      deferred_ack_message,
      parameters,
      ...(pre_requests.length > 0 ? { pre_requests } : {}),
      api_config,
    },
    errors: [],
  };
}

function recordToKeyValuePairs(record?: Record<string, string> | null): KeyValuePair[] {
  if (!record || typeof record !== 'object') return [];
  return Object.entries(record).map(([key, value]) => ({
    id: createId(),
    key,
    value: value ?? '',
  }));
}

function jsonToEditorString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

function mapAuthenticationToForm(
  auth?: CreateBusinessToolRequest['api_config']['authentication']
): AuthFormState {
  const base = createDefaultAuthState();
  if (!auth?.type) return base;
  return {
    ...base,
    type: auth.type,
    token: auth.token ?? '',
    api_key: auth.api_key ?? '',
    header_name: auth.header_name || 'X-API-Key',
    username: auth.username ?? '',
    password: auth.password ?? '',
    client_id: auth.client_id ?? '',
    client_secret: auth.client_secret ?? '',
    token_url: auth.token_url ?? '',
    scope: auth.scope ?? '',
    grant_type: auth.grant_type === 'authorization_code' ? 'authorization_code' : 'client_credentials',
    headers: recordToKeyValuePairs(auth.headers),
    credential_id: auth.credential_id ?? '',
  };
}

/** Maps GET /business-tools/{id} response into modal form state. */
export function mapBusinessToolDetailToForm(detail: BusinessToolDetail): BusinessToolFormState {
  const api = detail.api_config ?? ({} as CreateBusinessToolRequest['api_config']);
  const parameters: ParameterFormRow[] =
    Array.isArray(detail.parameters) && detail.parameters.length > 0
      ? detail.parameters.map((param) => ({
          id: createId(),
          name: param.name ?? '',
          type: param.type ?? 'string',
          description: param.description ?? '',
          required: param.required !== false,
          examples: param.examples ?? [],
        }))
      : [createEmptyParameter()];

  const pre_requests: PreRequestFormRow[] = Array.isArray(detail.pre_requests)
    ? detail.pre_requests.map((pre) => {
        const extractFields = recordToKeyValuePairs(pre.extract_fields);
        return {
          id: createId(),
          name: pre.name ?? '',
          description: pre.description ?? '',
          endpoint: pre.endpoint ?? '',
          method: pre.method ?? 'POST',
          headers: recordToKeyValuePairs(pre.headers),
          body_template: jsonToEditorString(pre.body_template),
          query_params: recordToKeyValuePairs(pre.query_params),
          extract_fields: extractFields.length > 0 ? extractFields : [createEmptyKeyValue()],
          timeout_seconds: pre.timeout_seconds ?? 10,
          cache_enabled: Boolean(pre.cache_config?.enabled),
          cache_key: pre.cache_config?.cache_key ?? '',
        };
      })
    : [];

  return {
    name: detail.name ?? '',
    description: detail.description ?? '',
    engaging_words: detail.engaging_words || 'Processing your request...',
    execution_mode: detail.execution_mode === 'deferred' ? 'deferred' : 'immediate',
    deferred_ack_message:
      detail.execution_mode === 'deferred' ? detail.deferred_ack_message ?? '' : '',
    parameters,
    pre_requests,
    base_url: api.base_url ?? '',
    endpoint: api.endpoint ?? '',
    method: api.method ?? 'POST',
    timeout_seconds: api.timeout_seconds ?? 30,
    query_params: recordToKeyValuePairs(api.query_params),
    body_template: jsonToEditorString(api.body_template),
    authentication: mapAuthenticationToForm(api.authentication),
    response_body_format: api.response_body_format === 'sse' ? 'sse' : 'json',
    sse_final_event_name: api.sse_final_event_name || 'workflow_finished',
    response_extract_path: api.response_extract_path ?? '',
    response_parse_inner_json: api.response_parse_inner_json === true,
    response_field_path: api.response_field_path ?? '',
    response_max_chars:
      api.response_max_chars != null && api.response_max_chars !== undefined
        ? String(api.response_max_chars)
        : '12000',
    success_message: api.success_message ?? '',
    error_message: api.error_message ?? '',
  };
}
